<template>
    <div>
        <team-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            @add="emit('add')"
            type="pending"
            :loading="loading"
            :readOnly="props.readOnly"
        ></team-users-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box
                color="info"
                v-if="!isEqual(defaultFilters(), fetchParams)"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                <div class="w-64 mx-auto text-center">
                    No results for current search, try adjusting your filters.
                </div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box
                color="info"
                v-else-if="!list.length"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                No pending users yet.
                <vx-button v-if="isAdminOrOwner && !props.readOnly" @click="emit('add')">
                    Add User <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
        </template>

        <vx-table
            v-if="list.length"
            :class="FEATURES.contact_groups ? 'xl:grid-cols-7-auto' : 'xl:grid-cols-5-auto'"
            rowBreakpoint="xl"
        >
            <vx-table-row isHeader>
                <div>Email</div>
                <div>Team Role</div>
                <div v-if="FEATURES.contact_groups">OU Invites</div>
                <div v-if="FEATURES.contact_groups">Group Invites</div>
                <div>Status</div>
                <div>Invited At</div>
            </vx-table-row>

            <team-pending-user-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                :teamId="props.teamId"
                @inviteToGroup="onInviteToGroup(list[index])"
                @inviteToOrgUnit="onInviteToOrgUnit(list[index])"
                @deleted="removeFromList($event)"
                :readOnly="props.readOnly"
            >
            </team-pending-user-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                {{ list.length }}/{{ props.totalCount }}
            </div>
        </div>

        <team-user-group-invite-modal
            v-if="FEATURES.contact_groups"
            :team-id="props.teamId"
            v-model="selectedUserForInvite"
            v-model:visible="showGroupInviteModal"
            @invitedToGroup="onInvitedToGroup($event)"
        >
        </team-user-group-invite-modal>
        <team-user-org-unit-invite-modal
            v-if="FEATURES.contact_groups"
            :team-id="props.teamId"
            v-model="selectedUserForInvite"
            v-model:visible="showOrgUnitInviteModal"
            @invitedToOrgUnit="onInvitedToOrgUnit($event)"
        >
        </team-user-org-unit-invite-modal>
    </div>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue';
import TeamPendingUserRow from './TeamPendingUserRow.vue';
import TeamUsersControls from './TeamUsersControls.vue';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCircleX, faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { onMounted, inject, ref } from 'vue';
import { isEqual } from '~/utils/helpers';
import { invitationsClient } from '~/services';
import TeamUserGroupInviteModal from './TeamUserGroupInviteModal.vue';
import TeamUserOrgUnitInviteModal from './TeamUserOrgUnitInviteModal.vue';

const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES');

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    totalCount: {
        type: Number,
        default: 0,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['add', 'deleted']);

const defaultFilters = () => ({
    cursor: null,
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 10,
    role: undefined,
    query: null,
});

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const loadData = () => {
    fetchRecords(invitationsClient.listInvites(props.teamId, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    emit('deleted', itemId);
    list.value = list.value.filter((item) => item.id != itemId);

    if (!list.value.length) {
        loadData();
    }
};

onMounted(() => {
    resetFilters();
});

defineExpose({
    loadData,
});

const selectedUserForInvite = ref({
    email: '',
    role: '',
});
const showGroupInviteModal = ref(false);
const onInviteToGroup = (user) => {
    selectedUserForInvite.value = user;
    showGroupInviteModal.value = true;
};

const onInvitedToGroup = () => {
    selectedUserForInvite.value = {
        email: '',
        role: '',
    };
    showGroupInviteModal.value = false;
};

const showOrgUnitInviteModal = ref(false);
const onInviteToOrgUnit = (user) => {
    selectedUserForInvite.value = user;
    showOrgUnitInviteModal.value = true;
};

const onInvitedToOrgUnit = () => {
    selectedUserForInvite.value = {
        email: '',
        role: '',
    };
    showOrgUnitInviteModal.value = false;
};
</script>
